/**
 * Route `/webAppOnboarding/course-not-certified-process-overview`. Shown after insurance
 * selection for non-certified courses. Blocked insurers keep the free-use content; invoice and
 * abtretung insurers see the Abtretung process overview.
 */
import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '../../../styles/colors';
import { space, textSize } from '../../../styles/onboardingUiTokens';
import { ListTimeline, ListTimelineItem } from '../components/content/ListTimeline';
import { SvgIcon } from '../components/icons/SvgIcon';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1 } from '../components/typography/HeadingLevels';
import { useCourseNotCertifiedGuard } from '../hooks/useCourseNotCertifiedGuard';
import { buildAbtretungExplainMeta, DEFAULT_BLOCKED_EXPLAIN } from '../insurance/copy';
import { resolveInsuranceOnboardingContext } from '../insurance/routing';
import { useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
  padding-bottom: ${space.xl};
`;

export function WebOnboardingCourseNotCertifiedProcessOverviewScreen() {
  useSaveOnboardingStep('insurance_blocked_free_process_overview');
  const navigate = useNavigate();
  const posthog = usePostHog();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);

  useCourseNotCertifiedGuard();

  const ctx = useMemo(
    () => resolveInsuranceOnboardingContext({ source: 'patient', patient: signedUpPatient }),
    [signedUpPatient],
  );
  const billingType = ctx.billingType;
  const insuranceName = ctx.insuranceName ?? '';

  const handleBack = useCallback(() => {
    navigate('/webAppOnboarding/insurance-select');
  }, [navigate]);

  const handleForward = () => {
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_process_overview_completed, {
      processType: 'courseNotCertified',
      billingType: billingType ?? undefined,
    });
    navigate('/webAppOnboarding/install');
  };

  const abtretungContent = (() => {
    if (!insuranceName) {
      return null;
    }
    const { timeline, headlineTemplate } = buildAbtretungExplainMeta(insuranceName);
    return (
      <ContentColumn>
        <H1>{headlineTemplate}</H1>
        <ListTimeline>
          <ListTimelineItem
            marker={
              <span aria-hidden style={{ fontSize: '1.125rem', lineHeight: 1 }}>
                💪
              </span>
            }
            circleBackground={colors.light}
            circleBorder={colors.light}
            numberColor={colors.white}
            title={timeline.step1Title}
          >
            {timeline.step1Body}
          </ListTimelineItem>
          <ListTimelineItem
            marker={<SvgIcon name="check" size={20} primaryColor={colors.white} />}
            circleBackground={colors.success}
            circleBorder={colors.success}
            numberColor={colors.white}
            title={timeline.step2Title}
          >
            {timeline.step2BodyTemplate}
          </ListTimelineItem>
        </ListTimeline>
      </ContentColumn>
    );
  })();

  const blockedContent = (
    <ContentColumn>
      <SvgIcon name="checkcircle" size={48} primaryColor={colors.success} />
      <H1>{DEFAULT_BLOCKED_EXPLAIN.title}</H1>
      <Body variant="document" style={{ fontSize: textSize.xl.fontSize, lineHeight: textSize.xl.lineHeight }}>
        {DEFAULT_BLOCKED_EXPLAIN.body}
      </Body>
    </ContentColumn>
  );

  return (
    <OnboardingLayout
      currentStep={4}
      totalSteps={5}
      onBack={handleBack}
      forwardLabel="Starten"
      onForward={handleForward}
    >
      <OnboardingHeader />
      <OnboardingBody>{billingType === 'blocked' ? blockedContent : abtretungContent}</OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
